/**
 * Stauaufschlag je Herkunfts-PLZ: Anwendung auf die Ergebniszeilen.
 *
 * Die View `cr_export_final` liefert die Fahrzeiten unveraendert und den
 * Aufschlag daneben als eigene Spalte (`PKW_Zeit` und `PKW_Stau`). Erst hier
 * werden beide zusammengefuehrt. Das hat drei Gruende:
 *
 * 1. Der Schalter in der Oberflaeche wirkt sofort, ohne neue Abfrage und ohne
 *    Neuberechnung in der Datenbank.
 * 2. Wer keine Aufschlaege pflegt, sieht exakt dieselben Zahlen wie vorher.
 * 3. Alles Nachgelagerte folgt automatisch: die "beste Alternative" wird in
 *    `mapOutputStore.calculateBestAlternatives` aus genau diesen `*_Zeit`-Feldern
 *    gerechnet, ebenso die Zeitspalten der Ergebnistabellen.
 *
 * Angewendet wird rein additiv: `Zeit + Stau`. Der Prozentanteil steckt bereits
 * im gelieferten Aufschlag, er ist in SQL auf die reine Fahrtzeit gerechnet.
 * Eine Sonderbehandlung fuer Fixschichten (die Wartezeit koennte den Stau
 * puffern) war geplant, traegt aber nicht: reine Strassen-Verkehrsmittel haben
 * mangels Fahrplan gar keine Wartezeit, und bei intermodalen Ketten kostet
 * zusaetzliche Strassenzeit eher den Anschluss, als dass sie im Puffer
 * verschwindet.
 */

/**
 * Spalten-Praefixe der Verkehrsmittel, wie sie `cr_export_final` fuehrt.
 * Quelle der Wahrheit ist der Pivot in der View `cr_export`; dieselbe Liste
 * steht im Backend in `table_controlls/plz_surcharge.py`.
 */
export const SURCHARGE_TRANSPORT_PREFIXES = [
  "PKW",
  "Pedelec",
  "Fahrrad",
  "OeV",
  "OeV_Fahrrad",
  "Auto_OeV",
  "Bahnreakt_Ride",
  "E_Auto",
  "Fuss_Weg",
  "BikeScooter",
  "Taxi",
  "Taxi_OeV",
  "eScooter",
  "eScooter_OeV",
  "Werksbus",
  "Fahrrad_Werksbus",
  "Park_Ride_and_Bike",
] as const;

/**
 * Praefix -> Bezeichner, den `Zeit_effektivstes_VM` verwendet.
 * Nicht identisch mit dem Praefix: die View schreibt fuer `OeV_Fahrrad`
 * beispielsweise `Bike_and_Ride`. Uebernommen aus der CASE-Kaskade in
 * `init.sql`, damit der neu berechnete Wert genau wie der aus SQL aussieht.
 */
const EFFECTIVE_TRANSPORT_LABELS: Record<string, string> = {
  PKW: "PKW",
  Pedelec: "Pedelec",
  Fahrrad: "Fahrrad",
  OeV: "OeV",
  OeV_Fahrrad: "Bike_and_Ride",
  Auto_OeV: "Park_and_Ride",
  Bahnreakt_Ride: "Bahnreakt_Ride",
  E_Auto: "E_Auto",
  Fuss_Weg: "Fuss_Weg",
  BikeScooter: "BikeScooter",
  Taxi: "Taxi",
  Taxi_OeV: "Taxi_and_Ride",
  eScooter: "eScooter",
  eScooter_OeV: "eScooter_and_Ride",
  Werksbus: "Werksbus",
  Fahrrad_Werksbus: "Fahrrad_Werksbus",
  Park_Ride_and_Bike: "Park_Ride_and_Bike",
};

type ResultRow = Record<string, any>;

/** Zahl aus einem Feld lesen; alles Unbrauchbare wird zu null. */
function toNumber(value: any): number | null {
  if (value === null || value === undefined || value === "") return null;
  const numeric = typeof value === "number" ? value : Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

/**
 * Zeit plus Aufschlag.
 *
 * Ohne Fahrzeit bleibt es bei null - ein Verkehrsmittel ohne Route darf durch
 * den Aufschlag nicht auf einen Zahlenwert springen, sonst gewinnt es jedes
 * Zeitranking. Ohne Aufschlag bleibt die Zeit unveraendert.
 */
export function addSurcharge(time: any, surcharge: any): number | null {
  const baseTime = toNumber(time);
  if (baseTime === null) return null;
  const extra = toNumber(surcharge);
  if (extra === null) return baseTime;
  return baseTime + extra;
}

/** True, wenn die Zeile ueberhaupt einen wirksamen Aufschlag traegt. */
export function rowHasSurcharge(row: ResultRow): boolean {
  return SURCHARGE_TRANSPORT_PREFIXES.some((prefix) => {
    const value = toNumber(row?.[`${prefix}_Stau`]);
    return value !== null && value > 0;
  });
}

/** True, wenn irgendeine Zeile des Ergebnisses einen Aufschlag traegt. */
export function resultsHaveSurcharge(
  rows: ResultRow[] | null | undefined,
): boolean {
  return Array.isArray(rows) && rows.some(rowHasSurcharge);
}

/**
 * Bestimmt das zeitlich effektivste Verkehrsmittel neu.
 *
 * Muss nach dem Aufschlag passieren: `Zeit_effektivstes_VM` kommt aus SQL und
 * kennt dort nur die Zeiten ohne Stau. Gleichstand wird wie in der View
 * aufgeloest - das erste Verkehrsmittel in der festen Reihenfolge gewinnt.
 */
export function recomputeEffectiveTransport(row: ResultRow): string | null {
  let bestLabel: string | null = null;
  let bestTime: number | null = null;

  for (const prefix of SURCHARGE_TRANSPORT_PREFIXES) {
    const time = toNumber(row[`${prefix}_Zeit`]);
    if (time === null) continue;
    if (bestTime === null || time < bestTime) {
      bestTime = time;
      bestLabel = EFFECTIVE_TRANSPORT_LABELS[prefix] ?? prefix;
    }
  }
  return bestLabel;
}

/**
 * Eine Ergebniszeile mit eingerechnetem Aufschlag.
 *
 * Gibt eine Kopie zurueck und laesst das Original unangetastet, damit der
 * Schalter beim Ausschalten wieder exakt die urspruenglichen Werte liefert.
 * Die `*_Stau`-Felder bleiben erhalten - die Oberflaeche weist den Aufschlag
 * hinter dem Zeitwert aus.
 */
export function applySurchargeToRow(row: ResultRow): ResultRow {
  const result: ResultRow = { ...row };

  for (const prefix of SURCHARGE_TRANSPORT_PREFIXES) {
    const timeKey = `${prefix}_Zeit`;
    if (!(timeKey in result)) continue;
    result[timeKey] = addSurcharge(result[timeKey], result[`${prefix}_Stau`]);
  }

  if ("Zeit_effektivstes_VM" in row) {
    result.Zeit_effektivstes_VM = recomputeEffectiveTransport(result);
  }
  return result;
}

/**
 * Alle Ergebniszeilen mit eingerechnetem Aufschlag.
 *
 * Traegt keine Zeile einen Aufschlag, wird die urspruengliche Liste
 * unveraendert zurueckgegeben - kein unnoetiges Kopieren und keine Chance auf
 * eine ungewollte Abweichung bei Standorten ohne Konfiguration.
 */
export function applySurchargeToResults(
  rows: ResultRow[] | null | undefined,
): ResultRow[] {
  if (!Array.isArray(rows) || rows.length === 0) return rows ?? [];
  if (!resultsHaveSurcharge(rows)) return rows;
  return rows.map(applySurchargeToRow);
}

/** Aufschlag eines Verkehrsmittels in einer Zeile, oder null. */
export function surchargeFor(row: ResultRow, prefix: string): number | null {
  const value = toNumber(row?.[`${prefix}_Stau`]);
  return value !== null && value > 0 ? value : null;
}
